import React, { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

// Generic type for sortable columns
export type SortDirection = "asc" | "desc";

export interface SortConfig<T> {
  column: keyof T | string;
  direction: SortDirection;
}

export interface ColumnDefinition<T> {
  id: keyof T | string;
  header: string;
  sortable?: boolean;
  cell: (item: T) => ReactNode;
  headerClassName?: string;
  cellClassName?: string;
}

export interface DataTableProps<T> {
  // Data
  data: T[];
  columns: ColumnDefinition<T>[];
  
  // Sorting
  sortConfig?: SortConfig<T>;
  onSort?: (column: keyof T | string) => void;
  
  // Pagination
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  
  // Selection (optional)
  selectable?: boolean;
  selectedItems?: Set<string>;
  onSelectAll?: (checked: boolean) => void;
  onSelectItem?: (itemId: string, checked: boolean) => void;
  getItemId?: (item: T) => string;
  
  // Row click (optional)
  onRowClick?: (item: T) => void;
  
  // Empty state
  emptyState?: ReactNode;
  
  // Loading state
  isLoading?: boolean;
  
  // Styling
  className?: string;
  striped?: boolean;
}

export function DataTable<T>({
  data,
  columns,
  sortConfig,
  onSort,
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  selectable = false,
  selectedItems,
  onSelectAll,
  onSelectItem,
  getItemId,
  onRowClick,
  emptyState,
  isLoading = false,
  className = "",
  striped = true,
}: DataTableProps<T>) {
  // Calculate pagination display values
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Check if all items on current page are selected
  const allPageItemsSelected =
    selectable &&
    selectedItems &&
    data.length > 0 &&
    getItemId &&
    data.every((item) => selectedItems.has(getItemId(item)));

  const somePageItemsSelected =
    selectable &&
    selectedItems &&
    getItemId &&
    data.some((item) => selectedItems.has(getItemId(item))) &&
    !allPageItemsSelected;

  // Render sort icon
  const renderSortIcon = (columnId: keyof T | string) => {
    if (!sortConfig || sortConfig.column !== columnId) {
      return null;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 inline ml-1" />
    );
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: string) => {
    onItemsPerPageChange(Number(value));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Scrollable table container */}
      <div className="overflow-x-auto border rounded-md">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            <TableRow>
              {/* Selection column */}
              {selectable && (
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={allPageItemsSelected}
                    onCheckedChange={onSelectAll}
                    aria-label="Select all items on this page"
                    className={somePageItemsSelected ? "data-[state=checked]:bg-primary/50" : ""}
                  />
                </TableHead>
              )}

              {/* Data columns */}
              {columns.map((column) => (
                <TableHead
                  key={String(column.id)}
                  className={`font-semibold ${column.headerClassName || ""} ${
                    column.sortable && onSort ? "cursor-pointer hover:bg-muted/50 transition-colors" : ""
                  }`}
                  onClick={() => column.sortable && onSort && onSort(column.id)}
                >
                  {column.header}
                  {column.sortable && renderSortIcon(column.id)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => {
              const itemId = getItemId ? getItemId(item) : String(index);
              const isSelected = selectedItems?.has(itemId) || false;

              return (
                <TableRow
                  key={itemId}
                  className={`${
                    striped && index % 2 === 0 ? "bg-muted/30" : ""
                  } ${onRowClick ? "cursor-pointer hover:bg-muted/50 transition-colors" : ""}`}
                  onClick={() => onRowClick && onRowClick(item)}
                >
                  {/* Selection cell */}
                  {selectable && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          onSelectItem && onSelectItem(itemId, checked as boolean)
                        }
                        aria-label={`Select ${itemId}`}
                      />
                    </TableCell>
                  )}

                  {/* Data cells */}
                  {columns.map((column) => (
                    <TableCell
                      key={`${itemId}_${String(column.id)}`}
                      className={column.cellClassName || ""}
                    >
                      {column.cell(item)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between border-t bg-background px-4 py-3 mt-4 rounded-b-md">
        {/* Left side: Items count and per-page selector */}
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            Showing {startItem} to {endItem} of {totalItems} results
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Items per page:</span>
            <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
              <SelectTrigger className="w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Right side: Page navigation */}
        <div className="flex items-center gap-2">
          {/* First page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            aria-label="First page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>

          {/* Previous page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page numbers */}
          <div className="flex items-center gap-1">
            {getPageNumbers().map((page, index) => {
              if (page === "...") {
                return (
                  <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                    ...
                  </span>
                );
              }

              return (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => onPageChange(page as number)}
                  className="min-w-[40px]"
                >
                  {page}
                </Button>
              );
            })}
          </div>

          {/* Next page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            aria-label="Last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
